/**
 * Copyright 2025 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 */

import { createStore, type StoreApi } from 'zustand';
import type { PubSubNodeFilter } from './NodeFilters';
import {
  CompareNodeId,
  type ExpandedNodeId,
  NodeClassType,
  type NodeId,
  type QualifiedName,
  type ValueType,
} from './PubSubTypes';

/**
 * Adressraum aufbauen und mit Daten füllen
 * 1. Adressraum aus allen Metadatenobjekten aufbauen
 * 2. Warten bis vollständiger Adressraum erkannt wurde -> Event senden, ab jetzt kann man Query ausführen
 * 3. Pfade für Daten bestimmen und effizient hitnerlegen.
 *      Für jede Nachricht am Datentopic muss folgende Info verfügbar sein:
 *      Aus Topic wird das Set von Nodes bestimmt, das hier erwartet wird
 *      Dann gib es "Pfade" zu den Values
 *        Falls das nur eine Ebene ist, kann man mit dem Array Syntax [""] drauf zugreifen, falls mehrere Ebenen erforderlich sind
 *        kann man auch JSON-Path nutzen. Alle (geänderten, erste Version erst mal alle ) Werte markieren, dss es eine Änderung gegeben hat.
 *
 *  Änderungen von Werten können durch die externe Methode "Notify" kommuniziert werden. Diese sollte z.B. alle 1000ms aufgerufen werden. (Vielleicht auch öfters, je nach Anwendungsfall)
 *
 * Datenabfragen aus Adressraum
 * 1. Ausgehend vom Root-Node können Nodes identifiziert werden, hierfür kann ein Filter genutzt werden. Zugriff über custom hooks mit useEffect,
 *    Abfrage des Nodes nur initial notwendig, falls sich der Adressraum nicht geändert hat. (Änderungen als "Versionen von Nodes" abbilden?)
 * 2. Subscription auf identifizierte Nodes über Hooks und callback (oder geht das irgendwie mit Return?)
 */

/**
 * Generates the dashboard internal (!) string identifier of a node.
 * @param node node for identifier generation
 */
export type NodeIdentifier = string;

function _toUaNodeIdentifierExpNodeId(expNodeId: ExpandedNodeId): NodeIdentifier {
  function addNs0(expNId: ExpandedNodeId): ExpandedNodeId {
    if (expNId.namespaceUri !== undefined) return expNId;
    return {
      namespaceUri: 'http://opcfoundation.org/UA/',
      identifier: expNId.identifier,
    };
  }
  const normNsId = addNs0(expNodeId);
  return JSON.stringify(normNsId, Object.keys(normNsId).sort());
}

function _toUaNodeIdentifier(node: UaNode): NodeIdentifier {
  return _toUaNodeIdentifierExpNodeId(node.ExpandedNodeId);
}

function _toUaNodeIdentifierArray(nodes: UaNode[]): NodeIdentifier[] {
  return nodes.map(_toUaNodeIdentifier);
}

export namespace ns0 {
  export const HierarchicalReferences = 33;
  export const HasChild = 34;
  export const Organizes = 35;
  export const Aggregates = 44;
  export const HasSubtype = 45;
  export const HasProperty = 46;
  export const HasComponent = 47;
  export const HasOrderedComponent = 49;
  export const HasAddIn = 17604;
  export const FolderType = 61;
  export const HasTypeDefinition = 40;
}

export namespace nsIA {
  export const HasStatisticsComponent = 4002;
}

export interface NodeReference {
  ReferenceTypeId: NodeId;
  TargetNode: UaNode;
  isForward: boolean;
}

export interface ExternalNodeReference {
  ReferenceTypeId: NodeId;
  TargetNode: NodeId;
  isForward: boolean;
}

export class UaNode {
  public References: NodeReference[] = [];
  public ExternalReferences: ExternalNodeReference[] = [];
  public Version: number = 1;

  constructor(
    public readonly ExpandedNodeId: ExpandedNodeId,
    public readonly NodeId: NodeId,
    public readonly BrowseName: QualifiedName,
    public readonly nodeClass: NodeClassType,
  ) {}

  public AddReference(ref: NodeReference): boolean {
    const exists = this.References.some(
      (r) =>
        r.TargetNode === ref.TargetNode &&
        CompareNodeId(r.ReferenceTypeId, ref.ReferenceTypeId) &&
        r.isForward === ref.isForward,
    );
    if (exists) return false;
    this.References.push(ref);
    ++this.Version;
    return true;
  }

  public AddExternalReference(ref: ExternalNodeReference): boolean {
    const exists = this.ExternalReferences.some(
      (r) =>
        CompareNodeId(r.TargetNode, ref.TargetNode) &&
        CompareNodeId(r.ReferenceTypeId, ref.ReferenceTypeId) &&
        r.isForward === ref.isForward,
    );
    if (exists) return false;
    this.ExternalReferences.push(ref);
    ++this.Version;
    return true;
  }
}

type NodeValueChangeCallback = (value: ValueType) => void;

class NodeValue {
  public Version: number = 1;
  public Value: ValueType;
  private _callbacks: NodeValueChangeCallback[] = [];

  constructor(value: ValueType) {
    this.Value = value;
  }
  public UpdateValue(newValue: ValueType): boolean {
    if (this.Value === newValue) return false;
    this.Value = newValue;
    ++this.Version;
    return true;
  }
  public SubscribeToValueChanges(callback: NodeValueChangeCallback): () => void {
    this._callbacks.push(callback);
    return () => {
      const idx = this._callbacks.indexOf(callback);
      if (idx !== -1) this._callbacks.splice(idx, 1);
    };
  }
  public notifyValueChange(): void {
    for (const cb of this._callbacks) cb(this.Value);
  }
}

export type AddressSpaceChangeCallback = () => void;

// --- Zustand Store ---

interface PubSubAddressSpaceState {
  topicPath: string;
  namespaces: string[];
  rootNode?: UaNode;
  nodes: Record<NodeIdentifier, UaNode>;
  values: Record<NodeIdentifier, NodeValue>;
  changedValues: Record<NodeIdentifier, NodeValue>;
  addrChangeCallbacks: AddressSpaceChangeCallback[];
  addressSpaceReconstructed: boolean;

  // Actions
  setNamespaces: (namespaces: string[]) => void;
  getNamespaceUriById: (nsid: number) => string;
  getNamespaceIdByUri: (nsUri: string) => number | undefined;
  toExpandedNodeId: (nodeId: NodeId) => ExpandedNodeId;
  toNodeId: (expNodeId: ExpandedNodeId) => NodeId;
  createUaNodeById: (nodeId: NodeId, browseName: QualifiedName, nodeClass: NodeClassType) => UaNode;
  createUaNodeByExpId: (expNodeId: ExpandedNodeId, browseName: QualifiedName, nodeClass: NodeClassType) => UaNode;
  setRootNode: (node: UaNode) => void;
  addNode: (node: UaNode, override?: boolean) => NodeIdentifier;
  getNodeById: (id: NodeIdentifier) => UaNode | undefined;
  getNodeByNodeId: (nodeId: NodeId) => UaNode | undefined;
  addReference: (
    src: UaNode | NodeIdentifier,
    target: UaNode | NodeIdentifier,
    referenceType: NodeId,
    isForward: boolean,
  ) => boolean;
  addExternalReference: (
    src: UaNode | NodeIdentifier,
    target: NodeId,
    referenceType: NodeId,
    isForward: boolean,
  ) => boolean;
  getNodeIdsByFilter: (filter: PubSubNodeFilter, startingNodeId?: NodeIdentifier) => NodeIdentifier[];
  updateNodeValue: (nodeId: NodeIdentifier, value: ValueType) => boolean;
  notifyChanges: () => void;
  subscribeToNodeValueChange: (nodeId: NodeIdentifier, callback: NodeValueChangeCallback) => () => void;
  getNodeValue: (nodeId: NodeIdentifier) => ValueType | undefined;
  getValueVersion: (nodeId: NodeIdentifier) => number | undefined;
  subscribeToAddressSpaceChanges: (callback: AddressSpaceChangeCallback) => () => void;
  notifyAddressSpaceChange: () => void;
  setAddressSpaceReconstructed: (reconstructed?: boolean) => void;
  isAddressSpaceReconstructed: () => boolean;
  numberOfNodes: () => number;
  getNodeVersionById: (id: NodeIdentifier) => number | undefined;
  getNodeBrowseNameById: (id: NodeIdentifier) => QualifiedName | undefined;
  getRootNodeId: () => NodeIdentifier;
}

export type PubSubAddressSpace = StoreApi<PubSubAddressSpaceState>;

export const createPubSubAddressSpaceStore = (topicPath: string) =>
  createStore<PubSubAddressSpaceState>((set, get) => ({
    topicPath: topicPath,
    namespaces: [],
    rootNode: undefined,
    nodes: {},
    values: {},
    changedValues: {},
    addrChangeCallbacks: [],
    addressSpaceReconstructed: false,

    setNamespaces: (Namespaces) => set({ namespaces: Namespaces }),
    getNamespaceUriById: (nsid) => get().namespaces[nsid],
    getNamespaceIdByUri: (nsUri) => {
      const idx = get().namespaces.indexOf(nsUri);
      return idx === -1 ? undefined : idx;
    },
    toExpandedNodeId: (nodeId) => ({
      namespaceUri: get().getNamespaceUriById(nodeId.Namespace ?? 0),
      identifier: nodeId.Id,
    }),
    toNodeId: (expNodeId) => {
      const nsIdx = get().getNamespaceIdByUri(expNodeId.namespaceUri);
      if (nsIdx === undefined) throw 'Namespace uri not found.';
      return { Id: expNodeId.identifier, Namespace: nsIdx };
    },
    createUaNodeById: (nodeId, browseName, nodeClass) => {
      const expNodeId = get().toExpandedNodeId(nodeId);
      return new UaNode(expNodeId, nodeId, browseName, nodeClass);
    },
    createUaNodeByExpId: (expNodeId, browseName, nodeClass) => {
      const nodeId = get().toNodeId(expNodeId);
      return new UaNode(expNodeId, nodeId, browseName, nodeClass);
    },
    setRootNode: (node) => set({ rootNode: node }),
    getRootNodeId(): NodeIdentifier {
      return _toUaNodeIdentifier(get().rootNode!);
    },
    addNode: (node, override = false) => {
      const id = _toUaNodeIdentifier(node);
      set((state) => {
        if (id in state.nodes && !override) {
          const node = state.nodes[id];
          const values = { ...state.values };
          if (node.nodeClass === NodeClassType.VARIABLE && !(id in values)) {
            values[id] = new NodeValue(undefined);
          }
          return { ...state.nodes, values };
        } else {
          const nodes = { ...state.nodes, [id]: node };
          const values = { ...state.values };
          if (node.nodeClass === NodeClassType.VARIABLE && !(id in values)) {
            values[id] = new NodeValue(undefined);
          }
          return { nodes, values };
        }
      });
      return id;
    },
    getNodeById: (id) => get().nodes[id],
    getNodeByNodeId: (nodeId) => {
      const expNodeId = get().toExpandedNodeId(nodeId);
      return get().nodes[_toUaNodeIdentifierExpNodeId(expNodeId)];
    },
    getNodeVersionById: (id: NodeIdentifier): number | undefined => {
      return get().nodes[id]?.Version;
    },
    getNodeBrowseNameById: (id: NodeIdentifier): QualifiedName | undefined => {
      return get().nodes[id]?.BrowseName;
    },
    addReference: (src, target, referenceType, isForward) => {
      const nodeSrc = typeof src === 'string' ? get().getNodeById(src) : src;
      const nodeTarget = typeof target === 'string' ? get().getNodeById(target) : target;
      if (!nodeSrc) throw 'Src Node not found.';
      if (!nodeTarget) throw 'Target Node not found.';
      const refFwd: NodeReference = {
        isForward,
        ReferenceTypeId: referenceType,
        TargetNode: nodeTarget,
      };
      const refRev: NodeReference = {
        isForward: !isForward,
        ReferenceTypeId: referenceType,
        TargetNode: nodeSrc,
      };
      const successFwd = nodeSrc.AddReference(refFwd);
      const successRev = nodeTarget.AddReference(refRev);
      if (successFwd || successRev) get().notifyAddressSpaceChange();
      return successFwd || successRev;
    },
    addExternalReference: (src, target, referenceType, isForward) => {
      const nodeSrc = typeof src === 'string' ? get().getNodeById(src) : src;
      if (!nodeSrc) throw 'Src Node not found.';
      const ref: ExternalNodeReference = {
        isForward,
        ReferenceTypeId: referenceType,
        TargetNode: target,
      };
      const success = nodeSrc.AddExternalReference(ref);
      if (success) get().notifyAddressSpaceChange();
      return success;
    },
    getNodeIdsByFilter: (filter, startingNodeId) => {
      const startingNode = startingNodeId ? get().getNodeById(startingNodeId) : get().rootNode;
      if (!startingNode) throw 'Starting node not found.';
      return _toUaNodeIdentifierArray(filter(startingNode));
    },
    updateNodeValue: (nodeId, value) => {
      const values = { ...get().values };
      const nodeValue = values[nodeId];
      if (!nodeValue) {
        throw `Node with id ${nodeId} not found. Please add the node first.`;
      }
      const ret = nodeValue.UpdateValue(value);
      if (ret) {
        set((state) => ({
          changedValues: { ...state.changedValues, [nodeId]: nodeValue },
          values,
        }));
      }
      return ret;
    },
    notifyChanges: () => {
      const changed = get().changedValues;
      Object.values(changed).forEach((nv) => nv.notifyValueChange());
      set({ changedValues: {} });
    },
    subscribeToNodeValueChange: (nodeId, callback) => {
      const nodeValue = get().values[nodeId];
      if (!nodeValue) throw 'Node value not found.';
      return nodeValue.SubscribeToValueChanges(callback);
    },
    getNodeValue: (nodeId) => get().values[nodeId]?.Value,
    getValueVersion: (nodeId) => get().values[nodeId]?.Version,
    subscribeToAddressSpaceChanges: (callback) => {
      set((state) => ({
        addrChangeCallbacks: [...state.addrChangeCallbacks, callback],
      }));
      return () => {
        set((state) => ({
          addrChangeCallbacks: state.addrChangeCallbacks.filter((cb) => cb !== callback),
        }));
      };
    },
    notifyAddressSpaceChange: () => {
      get().addrChangeCallbacks.forEach((cb) => cb());
    },
    setAddressSpaceReconstructed: (reconstructed = true) => set({ addressSpaceReconstructed: reconstructed }),
    isAddressSpaceReconstructed: () => get().addressSpaceReconstructed,
    numberOfNodes(): number {
      return Object.keys(get().nodes).length;
    },
  }));
